import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { OTPInput } from "~/components/ui/OTPInput";
import { cn } from "~/lib/utils";

import type { ReactNode } from "react";

const DEFAULT_COOLDOWN_SECONDS = 60;

type OtpVerificationCardProps = {
  /** E.164 number the code went to. Always shown — see note below. */
  phone: string;
  onVerify: (code: string) => void | Promise<void>;
  onResend: () => void | Promise<void>;
  /** Omit to hide the "usar otro número" affordance. */
  onChangePhone?: () => void;
  isVerifying?: boolean;
  /** Rejection message from the API, rendered inside the card. */
  error?: string | null;
  cooldownSeconds?: number;
  /** Page-specific extras (Turnstile, legal copy) rendered under the input. */
  children?: ReactNode;
};

/**
 * The "escribí el código" step, shared by every surface that logs in with
 * WhatsApp OTP (login, register, checkout, course enrolment, preinscription).
 *
 * It exists because those five screens each grew their own version and drifted:
 * some showed the destination number, some had a resend cooldown, and NONE of
 * them showed the rejection inline — the failure went to a toast that is easy to
 * miss, so the code just silently did nothing.
 *
 * Three things this guarantees everywhere:
 *  - the destination number is ALWAYS visible, because "no llegó" is usually
 *    "went to the wrong number" and the learner cannot tell without seeing it;
 *  - a rejected code clears the boxes and states the reason in the card, next to
 *    where the person is already looking;
 *  - resend is time-gated rather than hidden, so the wait is visible instead of
 *    feeling like a dead end, with changing the number as the escape hatch.
 */
export function OtpVerificationCard({
  phone,
  onVerify,
  onResend,
  onChangePhone,
  isVerifying = false,
  error,
  cooldownSeconds = DEFAULT_COOLDOWN_SECONDS,
  children,
}: OtpVerificationCardProps) {
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  // A new rejection clears the boxes: the digits on screen are known-bad, and the
  // cleared state plus the message is what makes the retry obvious.
  useEffect(() => {
    if (error) setResetKey((k) => k + 1);
  }, [error]);

  const canResend = secondsLeft <= 0 && !isVerifying;

  const handleResend = async () => {
    if (!canResend) return;
    await onResend();
    setSecondsLeft(cooldownSeconds);
    setResetKey((k) => k + 1);
  };

  return (
    <div className="grid gap-4">
      <p className="text-center text-[14px] text-landing-muted">
        Enviamos un código por WhatsApp al{" "}
        <strong className="whitespace-nowrap text-landing-text">{phone}</strong>
      </p>

      <OTPInput onComplete={onVerify} disabled={isVerifying} resetKey={resetKey} />

      {error ? (
        <p
          role="alert"
          aria-live="assertive"
          className="flex items-start justify-center gap-2 rounded-lg border border-landing-red/30 bg-landing-red-50 px-3 py-2 text-left text-[13px] font-medium text-landing-red"
        >
          <AlertCircle size={16} className="mt-px shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}

      {isVerifying ? (
        <p className="text-center text-[13px] text-landing-muted">Verificando…</p>
      ) : null}

      {children}

      <div className="flex items-center justify-between gap-3 text-[13px]">
        {onChangePhone ? (
          <button
            type="button"
            onClick={onChangePhone}
            className="text-landing-muted underline transition-colors hover:text-landing-text"
          >
            Usar otro número
          </button>
        ) : (
          <span />
        )}

        <button
          type="button"
          onClick={handleResend}
          disabled={!canResend}
          className={cn(
            "underline transition-colors",
            canResend
              ? "text-landing-red hover:text-landing-red-hover"
              : "cursor-default text-landing-light no-underline",
          )}
        >
          {secondsLeft > 0 ? `Reenviar código en ${secondsLeft}s` : "Reenviar código"}
        </button>
      </div>
    </div>
  );
}
